import { ajax } from 'cadenza/api-client/api';
import { cadenzaUrl } from 'cadenza/utils/cadenza-url/cadenza-url';
import { MIMETYPE_DISY_SKETCH } from 'cadenza/api-client/api-constants';
import type { CoordinateDto } from 'cadenza/api-client/dto/coordinate-dto';
import { SketchFormat } from 'cadenza/map/classic-sketch-layer/sketch-format';
import { FillSignatureType, StyleProperty } from 'cadenza/map/style/style-constants';
import type { FillStyle } from 'cadenza/model/fill-style';

import type { Geometry } from 'ol/geom';
import type { Feature } from 'ol';

/** @see net/disy/services/sketch/dto/SketchPaletteDto.java */
interface SketchPaletteDto {
  id: string;
  printName: string;
  items: SketchPaletteItemDto[];
}

/** @see net/disy/services/sketch/dto/SketchPaletteItemDto.java */
interface SketchPaletteItemDto {
  id: number;
  previewImage: string;
  printName: string;
  type: string;
  style: SketchPaletteItemStyleDto;
}

interface SketchPaletteItemStyleDto {
  borderColor: string;
  lineWidth: number;
  foregroundColor: string;
  pointSignatureType: string;
  signatureSize: number;
  fontFamily: string;
  fontSize: string;
  bold: boolean;
  italic: boolean;
  /** @see net/disy/cadenza/graphics/style/properties/Anchor.java */
  anchor: string;
  /** @see de/disy/gis/gisterm/gfx/common/line/LineDecoration.java */
  lineStartDecoration: string;
  /** @see de/disy/gis/gisterm/gfx/common/line/LineDecoration.java */
  lineEndDecoration: string;

  imageAsBase64: string;
  image: HTMLImageElement;
  backgroundVisible: boolean;
  backgroundColor: string;
  lines?: string;
  rotation: number;

  fillSignatureType: FillSignatureType;
  fillStyle: FillStyle;
}

/** @see net/disy/services/sketch/dto/SketchLayerDto.java */
type SketchLayerDto = SketchPaletteItemDto[];

/** @see net/disy/services/sketch/dto/SketchObjectDto.java */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- not yet used from TS code
interface SketchObjectDto extends SketchPaletteItemDto {
  from: CoordinateDto;
  to: CoordinateDto;
  position: CoordinateDto;
  points: CoordinateDto[];
  radius: number[];
  lines: string[];
  image: string;
  group: string;
  paletteId: string;
  usedSketchPaletteItemId?: number;
}

export class ClassicSketchApi {

  _layerId: string;

  /* @see net/disy/services/sketch/rest/SketchPaletteEndpointController.java */
  static getPalettes () {
    return ajax<SketchPaletteDto[]>('/sketchpalettes', { dataType: 'json' });
  }

  /* @see net/disy/services/sketch/rest/SketchPaletteEndpointController.java */
  static getPalette (paletteId: string) {
    return ajax<SketchPaletteDto>('/sketchpalettes/' + paletteId, { dataType: 'json' })
      .then(async palette => {
        palette.items = await transformItemsStyleIfNeeded(palette.items);
        return palette;
      });
  }

  /* @see net/disy/services/sketch/rest/SketchLayerEndpointController.java */
  static addLayer (mapId: string) {
    return ajax<string>(`/maps/${mapId}/layers`, {
      type: 'POST',
      contentType: MIMETYPE_DISY_SKETCH
    }).then(id => new ClassicSketchApi(id));
  }

  constructor (layerId: string) {
    this._layerId = layerId;
  }

  /* @see net/disy/services/sketch/rest/SketchLayerEndpointController.java */
  load () {
    return ajax<SketchLayerDto>(this.path, {
      dataType: 'json',
      headers: {
        'Accept': MIMETYPE_DISY_SKETCH
      }
    }).then(items => transformItemsStyleIfNeeded(items));
  }

  /* @see net/disy/services/sketch/rest/SketchLayerEndpointController.java */
  save (features: Feature<Geometry>[]) {
    const sketchFormat = new SketchFormat();
    const featuresAttributes = features.map(sketchFormat.writeFeature);
    return ajax<SketchLayerDto>(this.path, {
      type: 'PUT',
      contentType: MIMETYPE_DISY_SKETCH,
      data: JSON.stringify(featuresAttributes, (key, value) => {
        if (key !== 'image' && key !== StyleProperty.IMAGE) {
          return value;
        }
      })
    });
  }

  /* @see net/disy/services/sketch/rest/SketchLayerEndpointController.java */
  get path () {
    return `/layers/${this._layerId}`;
  }

  get url () {
    return cadenzaUrl(this.path);
  }

  get layerId () {
    return this._layerId;
  }

}

function transformItemsStyleIfNeeded (items: SketchLayerDto) {
  const itemStylePromises = items.map(item => mapSignatureImageToImageIfNeeded(item.style));

  return Promise.all(itemStylePromises).then(itemStyles => {
    itemStyles.forEach((itemStyle, index) => {
      items[index].style = itemStyle;
    });
    return items;
  });
}

/**
 * If the item contains a base64 signature image, transform this into an HTMLImage element, this is helpful for later
 * when we want to keep our api synchronous and loading the image would force us to make it asynchronous.
 *
 * @param itemStyle - The palette item
 * @return the promise resolving with the palette items whose signatureImage was changed from a base64 image to an HTMLImage element
 */
function mapSignatureImageToImageIfNeeded (itemStyle: SketchPaletteItemStyleDto): Promise<SketchPaletteItemStyleDto> {
  if (itemStyle[StyleProperty.FILL_SIGNATURE_TYPE] === FillSignatureType.IMAGED) {
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = () => resolve(itemStyle);
      // Here we convert the image data URI from the server to an HTMLImageElement instance.
      // The type assertion is OK, because for the rest of the system it will be an HTMLImageElement instance.
      image.src = itemStyle.imageAsBase64;
      itemStyle[StyleProperty.IMAGE] = image;
    });
  } else {
    return Promise.resolve(itemStyle);
  }
}
